import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { buildInviteUrl } from "@/config/appConfig";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Copy, Link, Loader2, UserPlus, Check, AlertCircle, Mail, Send } from "lucide-react";
import { buildCoachBillingPendingCopy } from "@/lib/billingOwnership";

interface InviteLeaderDialogProps {
  trigger?: React.ReactNode | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onInviteSent?: () => void;
  initialEmail?: string;
  navigateOnSuccess?: boolean;
}

// Explicit UI states - no silent fallbacks
type InviteUiState = "idle" | "loading" | "success" | "error";

const RESEND_COOLDOWN_SECONDS = 30;

export function InviteLeaderDialog({
  trigger,
  open: openProp,
  onOpenChange,
  onInviteSent,
  initialEmail = "",
  navigateOnSuccess = true,
}: InviteLeaderDialogProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [uiState, setUiState] = useState<InviteUiState>("idle");
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [coachRole, setCoachRole] = useState("");
  const [invitationMessage, setInvitationMessage] = useState("");
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsDirty, setDetailsDirty] = useState(false);
  const [coachName, setCoachName] = useState("Your Coach");
  const [leaderEmail, setLeaderEmail] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [lastSentEmail, setLastSentEmail] = useState<string | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Guard to prevent multiple simultaneous calls
  const inviteInFlight = useRef(false);
  const emailInFlight = useRef(false);

  const qaMode = import.meta.env.MODE !== "production";

  // Reset state when dialog closes
  const isControlled = typeof openProp === "boolean";
  const dialogOpen = isControlled ? openProp : open;

  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!isControlled) {
      setOpen(newOpen);
    }
    onOpenChange?.(newOpen);
    if (!newOpen) {
      setUiState("idle");
      setInviteToken(null);
      setErrorMessage(null);
      setCopied(false);
      setCoachRole("");
      setInvitationMessage("");
      setDetailsDirty(false);
      setCoachName("Your Coach");
      setLeaderEmail("");
      setIsSendingEmail(false);
      setLastSentEmail(null);
      setCooldownRemaining(0);
    }
  }, [isControlled, onOpenChange]);

  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const timer = setInterval(() => {
      setCooldownRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownRemaining]);

  // Main invite generation via RPC - called ONLY by explicit user action
  const generateInviteLink = useCallback(async () => {
    // Prevent duplicate calls
    if (inviteInFlight.current) {
      if (qaMode) {
        console.log("[InviteLeaderDialog] Call already in flight, ignoring");
      }
      return;
    }

    inviteInFlight.current = true;
    setUiState("loading");
    setInviteToken(null);
    setErrorMessage(null);

    try {
      if (qaMode) {
        console.log("[InviteLeaderDialog] Calling get_or_create_coach_invite RPC...");
      }

      const { data, error } = await supabase.rpc("get_or_create_coach_invite");

      if (error) {
        console.error("[InviteLeaderDialog] RPC error:", error);
        setUiState("error");
        setErrorMessage(error.message || "Failed to create invite link");
        inviteInFlight.current = false;
        return;
      }

      if (!data) {
        console.error("[InviteLeaderDialog] RPC returned null token");
        setUiState("error");
        setErrorMessage("Failed to create invite link - no token returned");
        inviteInFlight.current = false;
        return;
      }

      if (qaMode) {
        const origin = typeof window !== "undefined" ? window.location.origin : "unknown";
        console.log("[InviteLeaderDialog] Invite token received:", data);
        console.log("[InviteLeaderDialog] Invite token length:", data.length);
        console.log("[InviteLeaderDialog] Origin:", origin);
      }

      setInviteToken(data);
      setUiState("success");
    } catch (err: unknown) {
      console.error("[InviteLeaderDialog] Unexpected error:", err);
      setUiState("error");
      setErrorMessage(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      inviteInFlight.current = false;
    }
  }, [qaMode]);

  useEffect(() => {
    if (dialogOpen && user) {
      generateInviteLink();
    }
  }, [dialogOpen, generateInviteLink, user]);

  useEffect(() => {
    if (dialogOpen) setLeaderEmail(initialEmail);
  }, [dialogOpen, initialEmail]);

  useEffect(() => {
    const loadInviteDetails = async () => {
      if (!dialogOpen || !user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .maybeSingle();

      const { data: invite } = await supabase
        .from("coach_invites")
        .select("coach_name, coach_role, coach_invitation_message")
        .eq("coach_id", user.id)
        .maybeSingle() as { data: { coach_name?: string; coach_role?: string; coach_invitation_message?: string } | null; error: unknown };

      const resolvedCoachName =
        invite?.coach_name ||
        profile?.full_name ||
        (user.user_metadata?.full_name as string | undefined) ||
        "Your Coach";

      setCoachName(resolvedCoachName);
      setCoachRole(invite?.coach_role || "");
      setInvitationMessage(invite?.coach_invitation_message || "");
      setDetailsDirty(false);
    };

    loadInviteDetails();
  }, [dialogOpen, user]);

  const handleSaveInviteDetails = async () => {
    if (!user) return;
    setDetailsSaving(true);

    const payload: {
      coach_name: string | null;
      coach_role: string | null;
      coach_invitation_message: string | null;
    } = {
      coach_name: coachName?.trim() || null,
      coach_role: coachRole.trim() ? coachRole.trim() : null,
      coach_invitation_message: invitationMessage.trim()
        ? invitationMessage.trim()
        : null,
    };

    const { error } = await supabase
      .from("coach_invites")
      .update(payload)
      .eq("coach_id", user.id);

    if (error) {
      console.error("[InviteLeaderDialog] Failed to save invite details:", error);
      toast.error("Failed to save invitation details");
    } else {
      toast.success("Invitation details saved");
      setDetailsDirty(false);
    }

    setDetailsSaving(false);
  };

  // Handle explicit retry button click - runs RPC once per click
  const handleRetry = () => {
    generateInviteLink();
  };

  // Handle dialog trigger click - starts the flow
  const handleDialogOpen = () => {
    if (!dialogOpen) {
      if (!isControlled) {
        setOpen(true);
      } else {
        onOpenChange?.(true);
      }
      // Note: generateInviteLink will be called by the useEffect when dialogOpen changes
      if (!user) {
        setUiState("error");
        setErrorMessage("Please sign in to invite leaders.");
      }
    }
  };

  const getInviteUrl = () => {
    if (!inviteToken) return "";
    return buildInviteUrl(inviteToken);
  };

  const handleInviteSendSuccess = useCallback(
    async (sentEmail: string) => {
      toast.success(`Invitation sent to ${sentEmail}!`);

      // Clear optional message context after a successful invite send.
      if (user) {
        const { error: clearMessageError } = await supabase
          .from("coach_invites")
          .update({ coach_invitation_message: null })
          .eq("coach_id", user.id);
        if (clearMessageError) {
          console.error("[InviteLeaderDialog] Failed to clear invitation message:", clearMessageError);
        }
      }

      setInvitationMessage("");
      setDetailsDirty(false);
      setLastSentEmail(sentEmail);
      setCooldownRemaining(RESEND_COOLDOWN_SECONDS);
      setLeaderEmail("");
      handleOpenChange(false);
      onInviteSent?.();
      if (navigateOnSuccess) navigate("/coach/overview");
    },
    [handleOpenChange, navigate, navigateOnSuccess, onInviteSent, user]
  );

  const copyToClipboard = async () => {
    const url = getInviteUrl();
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Invite link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const sendInviteEmail = useCallback(async (email: string) => {
    if (emailInFlight.current) return;
    if (!inviteToken || !email) {
      toast.error("Please enter an email address");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    emailInFlight.current = true;
    setIsSendingEmail(true);

    try {
      const { data, error } = await supabase.functions.invoke("send-leader-invite", {
        body: {
          leaderEmail: email,
          inviteToken,
        },
      });

      if (error) {
        console.error("[InviteLeaderDialog] Error sending email:", error);
        toast.error(error.message || "Failed to send invitation email");
        setIsSendingEmail(false);
        return;
      }

      if (data?.success) {
        if (data.billingResolutionPending) {
          toast.info("This leader already has an individual paid Coach Veera plan", {
            description: buildCoachBillingPendingCopy(),
          });
        }
        await handleInviteSendSuccess(email);
      } else {
        toast.error(data?.error || "Failed to send invitation email");
      }
    } catch (err: unknown) {
      console.error("[InviteLeaderDialog] Error sending email:", err);
      toast.error(err instanceof Error ? err.message : "Failed to send invitation email");
    } finally {
      emailInFlight.current = false;
      setIsSendingEmail(false);
    }
  }, [handleInviteSendSuccess, inviteToken]);

  const handleSendEmail = async () => {
    await sendInviteEmail(leaderEmail);
  };

  const handleResendEmail = async () => {
    const resendEmail = lastSentEmail || leaderEmail;
    await sendInviteEmail(resendEmail);
  };

  const resendDisabled =
    isSendingEmail || cooldownRemaining > 0 || (!lastSentEmail && !leaderEmail);

  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      {trigger !== null ? (
        <DialogTrigger asChild onClick={handleDialogOpen}>
          {trigger || (
            <Button>
              <UserPlus className="mr-2 h-4 w-4" />
              Invite Leader
            </Button>
          )}
        </DialogTrigger>
      ) : null}
      <DialogContent className="max-w-md h-[85vh] max-h-[85vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>{initialEmail ? "Re-invite a leader" : "Invite a Leader"}</DialogTitle>
          <DialogDescription>
            Share this link with a leader to invite them to your coaching program. They'll see a sign-up form,
            then be guided into their first moment review flow.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
          <div className="space-y-4 pt-2 pb-4">
          {/* LOADING STATE */}
          {uiState === "loading" && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* ERROR STATE - retry button */}
          {uiState === "error" && (
            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-destructive font-medium mb-3">
                    {errorMessage}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleRetry}>
                      Try Again
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setOpen(false)}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SUCCESS STATE */}
          {uiState === "success" && inviteToken && (
            <>
              <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
                <div>
                  <p className="text-sm font-medium text-foreground">Invitation context (optional)</p>
                  <p className="text-xs text-muted-foreground">
                    This will appear on your leader&apos;s Today page after they accept.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coach-role">Your role or title</Label>
                  <Input
                    id="coach-role"
                    placeholder="Executive Coach"
                    value={coachRole}
                    onChange={(event) => {
                      setCoachRole(event.target.value);
                      setDetailsDirty(true);
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-message">Invitation message</Label>
                  <Textarea
                    id="invite-message"
                    placeholder="Share why you're inviting them to Veera..."
                    value={invitationMessage}
                    onChange={(event) => {
                      setInvitationMessage(event.target.value);
                      setDetailsDirty(true);
                    }}
                    rows={4}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Sending as {coachName}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSaveInviteDetails}
                    disabled={!detailsDirty || detailsSaving}
                  >
                    {detailsSaving ? "Saving..." : "Save details"}
                  </Button>
                </div>
              </div>

              {/* Instructions */}
              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-start gap-2">
                  <Link className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">
                      Share this link with your leader
                    </p>
                    <p>
                      When they sign up using this link, they'll be
                      automatically connected to you as their coach.
                    </p>
                  </div>
                </div>
              </div>

              {/* Email Section */}
              <div className="space-y-3 pt-2 border-t border-border">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground">
                    Send invite via email
                  </p>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    aria-label="Leader email"
                    placeholder="leader@example.com"
                    value={leaderEmail}
                    onChange={(e) => setLeaderEmail(e.target.value)}
                    disabled={isSendingEmail || cooldownRemaining > 0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && leaderEmail) {
                        e.preventDefault();
                        handleSendEmail();
                      }
                    }}
                  />
                  <Button
                    onClick={handleSendEmail}
                    disabled={!leaderEmail || isSendingEmail || cooldownRemaining > 0}
                    size="icon"
                    className="shrink-0"
                    aria-label="Send invite email"
                  >
                    {isSendingEmail ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <div className="space-y-2 text-xs text-muted-foreground">
                  <p>If you do not see the email in a few minutes, check spam or promotions.</p>
                  <p>
                    {cooldownRemaining > 0
                      ? `You can resend in ${cooldownRemaining} seconds.`
                      : "You can resend the invite."}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleResendEmail}
                    disabled={resendDisabled}
                  >
                    Resend invite
                  </Button>
                </div>
              </div>

              {/* Copy button (large) */}
              <Button onClick={copyToClipboard} className="w-full">
                {copied ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Invite Link
                  </>
                )}
              </Button>
            </>
          )}

          {/* IDLE STATE - should not be visible when dialog is open */}
          {uiState === "idle" && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
